import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

interface UseVirtualScrollRestorationOptions {
  /** Additional key segment (e.g., streamId) to differentiate scroll positions */
  keySegment?: string;
}

/**
 * Hook for managing scroll restoration in virtualized lists.
 *
 * Saves and restores the first visible row index for scroll restoration.
 *
 * Usage:
 * ```tsx
 * const { onVisibleIndexChange, restoreToIndex } = useVirtualScrollRestoration({
 *   keySegment: activeStreamId, // Optional: differentiate by stream/filter
 * });
 *
 * <WindowVirtualGrid
 *   onVisibleIndexChange={onVisibleIndexChange}
 *   restoreToIndex={restoreToIndex}
 *   // ...
 * />
 * ```
 */
export function useVirtualScrollRestoration(
  options: UseVirtualScrollRestorationOptions = {},
) {
  const { keySegment } = options;
  const location = useLocation();
  const navigationType = useNavigationType();

  // Build storage key - includes pathname and optional segment
  const scrollKey = keySegment
    ? `scrollIndex:${location.pathname}:${keySegment}`
    : `scrollIndex:${location.pathname}`;

  // Save scroll index to sessionStorage on scroll
  const handleVisibleIndexChange = useCallback(
    (index: number) => {
      try {
        sessionStorage.setItem(scrollKey, String(index));
      } catch (e) {
        // Silently fail if sessionStorage is unavailable (e.g., in iframe, incognito mode)
        // Scroll restoration is a non-critical feature
      }
    },
    [scrollKey],
  );

  // Restore index on POP navigation (back/forward)
  const [restoreToIndex, setRestoreToIndex] = useState<number | undefined>();

  useEffect(() => {
    if (navigationType === "POP") {
      try {
        const saved = sessionStorage.getItem(scrollKey);
        if (saved) {
          const index = parseInt(saved, 10);
          if (!isNaN(index) && index > 0) {
            setRestoreToIndex(index);
          }
        }
      } catch (e) {
        // Silently fail if sessionStorage is unavailable (e.g., in iframe, incognito mode)
        // Scroll restoration is a non-critical feature
      }
    } else {
      // Fresh navigation - clear any restore state
      setRestoreToIndex(undefined);
    }
  }, [navigationType, scrollKey]);

  return {
    /** Pass to WindowVirtualGrid to track first visible index */
    onVisibleIndexChange: handleVisibleIndexChange,
    /** Pass to WindowVirtualGrid to restore scroll position on back navigation */
    restoreToIndex,
  };
}
